import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';

const QuestTimeline = ({ formData, questPlan }) => {
  const navigate = useNavigate();
  const [titleOpacity, setTitleOpacity] = useState(0);
  const [badgesOpacity, setBadgesOpacity] = useState(0);
  const [tableOpacity, setTableOpacity] = useState(0);
  const [buttonOpacity, setButtonOpacity] = useState(0);
  const { userProfile, setQuestPlan } = useContext(GlobalContext);

  useEffect(() => {
    // Fade in title after component mounts
    setTimeout(() => setTitleOpacity(1), 500);

    // Fade in badges after title
    setTimeout(() => setBadgesOpacity(1), 1000);

    // Fade in table after badges
    setTimeout(() => setTableOpacity(1), 1500);

    // Fade in button after table
    setTimeout(() => setButtonOpacity(1), 2000);
  }, []);

  // Calculate totals
  const totalLeetCode = questPlan.reduce((sum, week) => sum + week.tasks.leetcode.length, 0);
  const totalApps = questPlan.reduce((sum, week) => sum + week.tasks.apps.length, 0);

  // Group weeks by month
  const groupByMonth = () => {
    const months = {};
    questPlan.forEach((week) => {
      const date = new Date(week.week_start);
      const monthKey = date.toLocaleString('default', { month: 'long' });

      if (!months[monthKey]) {
        months[monthKey] = {
          leetcode_goal: week.tasks.leetcode.length,
          app_goal: week.tasks.apps.length,
          extra_tasks: new Set(),
        };
      } else {
        months[monthKey].leetcode_goal = week.tasks.leetcode.length;
        months[monthKey].app_goal = week.tasks.apps.length;
      }

      // Add lessons and resume tasks if they exist
      if (week.tasks.lessons.length > 0) {
        months[monthKey].extra_tasks.add('lessons');
      }
      if (week.tasks.resume.length > 0) {
        months[monthKey].extra_tasks.add('resume');
      }
    });
    return months;
  };
  const months = groupByMonth();

  // Submit the quest plan to the backend
  const submitQuestPlan = async () => {
    // Format dates to be date-only strings
    const formattedQuestPlan = questPlan.map((week) => ({
      ...week,
      week_start:
        typeof week.week_start === 'string'
          ? week.week_start.split('T')[0]
          : week.week_start.toISOString().split('T')[0],
    }));

    const startDate = questPlan[0]?.week_start
      ? typeof questPlan[0].week_start === 'string'
        ? questPlan[0].week_start.split('T')[0]
        : questPlan[0].week_start.toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

    console.log('Submitting quest plan with start date:', startDate);

    const quest = {
      user_id: userProfile.user_id,
      hours_per_week: formData.hoursPerWeek,
      grad_year: formData.gradYear,
      start_date: startDate,
      quest_plan: formattedQuestPlan,
      allow_emails: formData.emailUpdates,
    };
    try {
      const response = await axios.post('/api/v1/quest/create', {
        quest,
      });
      console.log('Quest plan submitted successfully');
      // Get quest
      setQuestPlan(quest);
    } catch (error) {
      console.error('Failed to submit quest plan:', error.response?.data || error.message);
    }
  };

  return (
    <div className="flex items-center justify-center h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto py-8">
        {/* Title Section */}
        <div
          className="flex flex-row items-center justify-center"
          style={{
            opacity: titleOpacity,
            transition: 'opacity 1s ease-in-out',
          }}
        >
          <h2 className="text-4xl font-bold text-black mb-5">
            🗺️ Your personalized quest goals! 🗺️
          </h2>
        </div>

        {/* Badges Section */}
        <div
          className="flex flex-wrap gap-4 mb-6 justify-center"
          style={{
            opacity: badgesOpacity,
            transition: 'opacity 1s ease-in-out',
          }}
        >
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">🎓</span>
            <span className="font-medium">Class of {formData.gradYear}</span>
          </div>
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">⏱️</span>
            <span className="font-medium">{formData.hoursPerWeek} hours/week</span>
          </div>
          <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">📧</span>
            <span className="font-medium">
              {formData.emailUpdates ? 'Weekly Updates' : 'No Updates'}
            </span>
          </div>
          <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">💻</span>
            <span className="font-medium">{totalLeetCode} Leetcode Problems</span>
          </div>
          <div className="bg-rose-100 text-rose-800 px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">📝</span>
            <span className="font-medium">{totalApps} Job Apps</span>
          </div>
        </div>

        {/* Table Section */}
        <div
          className="bg-white rounded-xl shadow-lg overflow-hidden mb-8"
          style={{
            opacity: tableOpacity,
            transition: 'opacity 1s ease-in-out',
          }}
        >
          <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
            <div className="max-h-[500px] overflow-y-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-500 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-lg font-bold text-white">
                      Month
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-lg font-bold text-white">
                      Your Tasks
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-lg font-bold text-white">
                      General recruiting cycle
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(months).map(([month, data]) => (
                    <tr key={month} className="hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-lg font-medium text-gray-900">
                        {month}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        <div className="space-y-2">
                          <div className="bg-blue-100 px-2 py-1 rounded-md">
                            <span className="mr-2">📊</span> {data.leetcode_goal} LeetCode problems
                            per week
                          </div>
                          <div className="bg-green-100 px-2 py-1 rounded-md">
                            <span className="mr-2">📝</span> {data.app_goal} applications per week
                          </div>
                          {Array.from(data.extra_tasks).map((task, index) => (
                            <div key={index} className="bg-purple-100 px-2 py-1 rounded-md">
                              <span className="mr-2">📋</span>{' '}
                              {task === 'resume'
                                ? 'Finalize your resume'
                                : task === 'lessons'
                                  ? 'Learn about the process'
                                  : task}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{getRecruitingCycleInfo(month)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-center">
              <div className="animate-bounce bottom-4">
                <svg
                  className="w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            style={{
              opacity: buttonOpacity,
              transition: 'opacity 1s ease-in-out',
            }}
            onClick={() => {
              submitQuestPlan();
              navigate('/');
            }}
            className="px-10 py-5 bg-linear-to-r from-blue-500 to-pink-400 text-white rounded-xl text-xl font-medium hover:from-blue-600 hover:to-pink-500 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-md active:shadow-inner"
          >
            I'm ready! 🚀
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to get recruiting cycle information
const getRecruitingCycleInfo = (month) => {
  switch (month.toLowerCase()) {
    case 'may':
    case 'june':
      return 'Very few applications are open';
    case 'july':
      return 'Applications begin opening';
    case 'august':
      return 'Many applications open, begin interviewing';
    case 'september':
      return 'Many applications open, interview season';
    case 'october':
      return 'Many applications open, interview & offer season';
    case 'november':
    case 'december':
      return 'Application season dies down, interview & offer season';
    default:
      return 'Regular recruiting season';
  }
};

export default QuestTimeline;
